import React, { useState } from 'react';
import { Box, Typography, Card, Container, Chip, Button, Grid } from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import WebIcon from '@mui/icons-material/Web';
import CloudIcon from '@mui/icons-material/Cloud';

const techPillars = [
  {
    title: 'AI Integration & Intelligence',
    icon: <IntegrationInstructionsIcon />,
    outcome: 'Transform your business processes with intelligent automation',
    metric: { value: '40%', label: 'Cost Reduction', desc: 'Through smart automation' },
    keyTechnologies: ['OpenAI API', 'Anthropic Claude', 'Custom ML Models', 'Predictive Analytics'],
    allTechnologies: [
      'OpenAI GPT-4 & ChatGPT', 'Anthropic Claude', 'Azure AI Services', 
      'Custom ML Models', 'RAG Systems', 'Vector Databases',
      'Real-time Analytics', 'Predictive Forecasting', 'Automated Reporting'
    ]
  },
  {
    title: 'Modern Application Development',
    icon: <WebIcon />,
    outcome: 'Build fast, reliable applications that users love',
    metric: { value: '99.9%', label: 'Uptime', desc: 'Rock-solid reliability' },
    keyTechnologies: ['React 19', 'FastAPI', 'TypeScript', 'Microservices'],
    allTechnologies: [
      'React 19 & Next.js 15', 'TypeScript', 'Joy-UI Components',
      'FastAPI', 'Go Services', 'GraphQL APIs',
      'PWA Capabilities', 'Real-time Features', 'Smart UX Design'
    ]
  },
  {
    title: 'Enterprise Cloud Infrastructure',
    icon: <CloudIcon />,
    outcome: 'Scale effortlessly with enterprise-grade reliability',
    metric: { value: '24/7', label: 'Monitoring', desc: 'Proactive issue prevention' },
    keyTechnologies: ['AWS Cloud', 'Kubernetes', 'Auto-scaling', 'CI/CD'],
    allTechnologies: [
      'AWS Cloud Platform', 'Kubernetes Orchestration', 'Auto-scaling',
      'CI/CD Pipelines', 'Infrastructure as Code', 'Security Monitoring',
      'Cost Optimization', 'Backup & Recovery', 'Performance Tuning'
    ]
  }
];

export const TechStackSection = () => {
  const [expandedPillar, setExpandedPillar] = useState(null);

  return (
    <Box id="tech-stack" sx={{ py: 12 }}>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <Typography 
              level="h2" 
              sx={{ 
                mb: 3,
                fontWeight: 700,
                color: 'text.primary'
              }}
            >
              Our Technology Foundation
            </Typography>
            <Typography 
              level="h4" 
              sx={{ 
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              Three core pillars that power exceptional business results
            </Typography>
          </Box>
        </motion.div>

        {/* Technology Pillars */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {techPillars.map((pillar, index) => (
            <motion.div
              key={pillar.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              <Card
                sx={{
                  p: 6,
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                  }
                }}
              >
                <Grid container spacing={4} alignItems="center">
                  {/* Left: Icon, Title, Outcome */}
                  <Grid xs={12} md={8}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #2E5BBA 0%, #00A86B 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          flexShrink: 0,
                          '& svg': { fontSize: '2rem' }
                        }}
                      >
                        {pillar.icon}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          level="h3" 
                          sx={{ 
                            color: 'text.primary',
                            fontWeight: 600,
                            mb: 2
                          }}
                        >
                          {pillar.title}
                        </Typography>
                        <Typography 
                          level="h4" 
                          sx={{ 
                            color: 'text.secondary',
                            fontWeight: 400,
                            mb: 3,
                            lineHeight: 1.6
                          }}
                        >
                          {pillar.outcome}
                        </Typography>
                        
                        {/* Key Technologies */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                          {pillar.keyTechnologies.map((tech, techIndex) => (
                            <Chip
                              key={tech}
                              variant="solid"
                              sx={{
                                background: 'rgba(255, 255, 255, 0.9)',
                                color: '#1B365D',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                fontWeight: 600,
                                '&:hover': {
                                  background: 'rgba(255, 255, 255, 1)',
                                  color: '#1B365D'
                                }
                              }}
                            >
                              {tech}
                            </Chip>
                          ))}
                        </Box>

                        {/* Expandable Technologies */}
                        <Button
                          variant="plain"
                          size="sm"
                          onClick={() => setExpandedPillar(expandedPillar === index ? null : index)}
                          sx={{ 
                            color: 'text.secondary',
                            '&:hover': { color: 'text.primary' }
                          }}
                        >
                          {expandedPillar === index ? 'Show Less' : `View All Technologies (${pillar.allTechnologies.length})`}
                        </Button>

                        <AnimatePresence>
                          {expandedPillar === index && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                  {pillar.allTechnologies.map((tech, techIndex) => (
                                    <Chip
                                      key={tech}
                                      variant="outlined"
                                      size="sm"
                                      sx={{
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        color: '#E2E8F0',
                                        border: '1px solid rgba(255, 255, 255, 0.3)',
                                        fontSize: '0.8rem',
                                        fontWeight: 500,
                                        '&:hover': {
                                          background: 'rgba(255, 255, 255, 0.1)',
                                          color: '#F1F5F9',
                                          border: '1px solid rgba(255, 255, 255, 0.5)'
                                        }
                                      }}
                                    >
                                      {tech}
                                    </Chip>
                                  ))}
                                </Box>
                              </Box>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Right: Metric */}
                  <Grid xs={12} md={4}>
                    <Box sx={{ textAlign: { xs: 'left', md: 'center' } }}>
                      <Typography 
                        level="h1" 
                        sx={{ 
                          fontSize: '3rem',
                          fontWeight: 700,
                          background: 'linear-gradient(135deg, #2E5BBA 0%, #00A86B 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          mb: 1
                        }}
                      >
                        {pillar.metric.value}
                      </Typography>
                      <Typography 
                        level="h4" 
                        sx={{ 
                          color: 'text.primary',
                          fontWeight: 600,
                          mb: 1
                        }}
                      >
                        {pillar.metric.label}
                      </Typography>
                      <Typography 
                        level="body2" 
                        sx={{ 
                          color: 'text.secondary'
                        }}
                      >
                        {pillar.metric.desc}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            </motion.div>
          ))}
        </Box>
      </Container>
    </Box>
  );
};